import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LineChart as LineChartIcon, RefreshCw } from "lucide-react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from "recharts";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (v, dp = 1) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;

const Stat = ({ label, value, sub, accent, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

const AttrList = ({ title, rows, keyField, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs font-semibold text-muted-foreground mb-1.5">{title}</div>
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r[keyField]} className="flex items-center justify-between text-xs">
          <span className="truncate pr-2">{r[keyField]}</span>
          <span className={`font-mono ${
            (r.contribution ?? r.weight) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
            {pct(r.contribution ?? r.weight)}
            {r.n_events != null && (
              <span className="text-muted-foreground ml-1">({r.n_events})</span>
            )}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const SignalBacktestPanel = ({ slug, cacheKey, lineColor, accent,
                                      benchLabel = "SPY" }) => {
  const { token } = useAuth();
  const [bt, setBt] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/signals/${slug}/backtest`, {
      headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { setMissing(false); return res.json(); }
    if (res.status === 404) setMissing(true);
    return null;
  }, [token, slug]);

  useEffect(() => {
    load().then((d) => d && setBt(d));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [load]);

  const run = async () => {
    setBusy(true);
    const prev = bt?.computed_at;
    const res = await fetch(`${API}/signals/${slug}/backtest/refresh`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Failed to start backtest"); setBusy(false); return; }
    toast.info("Backtest running — this takes a minute or two");
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      const d = await load();
      if (d && d.computed_at !== prev) {
        clearInterval(pollRef.current);
        setBt(d); setBusy(false);
        toast.success("Backtest complete");
      } else if (tries > 60) {
        clearInterval(pollRef.current);
        setBusy(false);
        toast.error("Backtest still not ready — check back later");
      }
    }, 5000);
  };

  const split = bt?.split || "2024-07-01";
  const a = bt?.attribution;

  const series = useMemo(() => {
    if (!bt?.nav_series) return [];
    const oos = bt.nav_series.filter((p) => p.date >= split);
    if (!oos.length) return bt.nav_series;
    const n0 = oos[0].nav || 1, s0 = oos[0].spy || 1;
    return oos.map((p) => ({ ...p, nav: (p.nav / n0) * 100, spy: (p.spy / s0) * 100 }));
  }, [bt, split]);

  const S = bt?.oos_stats
    ? { ...bt.oos_stats, excess_total: bt.oos_stats.total - bt.oos_stats.spy_total,
        since: split }
    : bt ? { ...bt.stats, since: bt.start } : null;

  return (
    <Card data-testid={`${slug}-backtest-panel`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <LineChartIcon className={`w-4 h-4 ${accent}`} />
            Portfolio backtest
            <span className="text-muted-foreground font-normal">
              — next-session entry, 63-session hold
            </span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={run} disabled={busy}
                  data-testid={`${slug}-backtest-run-btn`}>
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
            {missing ? "Run backtest" : "Recompute"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!bt ? (
          <p className="text-sm text-muted-foreground py-4">
            {missing ? "Not computed yet — hit Run backtest." : "Loading…"}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Stat label="Total return" value={pct(S.total)}
                    sub={`since ${S.since}`} accent={accent}
                    testId={`${slug}-bt-total`} />
              <Stat label={`vs ${benchLabel}`} value={pct(S.excess_total)}
                    sub={`${benchLabel} ${pct(S.spy_total)}`}
                    accent={S.excess_total >= 0 ? "text-emerald-300" : "text-rose-300"}
                    testId={`${slug}-bt-excess`} />
              <Stat label="Sharpe" value={S.sharpe ?? "—"}
                    sub={`vol ${pct(S.vol)}`} testId={`${slug}-bt-sharpe`} />
              <Stat label="Max drawdown" value={pct(S.max_dd)}
                    accent="text-rose-300" testId={`${slug}-bt-maxdd`} />
              <Stat label="Full history" value={pct(bt.stats.total)}
                    sub={`since ${bt.start} · ${benchLabel} ${pct(bt.stats.spy_total)} · cold-start grader pre Jul-24`}
                    accent={bt.stats.excess_total >= 0 ? "text-emerald-300" : "text-rose-300"}
                    testId={`${slug}-bt-oos`} />
              <Stat label="Positions" value={bt.avg_open_positions}
                    sub={`avg open · ${bt.open_positions_now} now · invested ${pct(bt.invested_share, 0)} of days`}
                    testId={`${slug}-bt-positions`} />
            </div>
            <div className="h-72" data-testid={`${slug}-bt-chart`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8b8fa3" }}
                         tickFormatter={(d) => d?.slice(2, 7)} minTickGap={48} />
                  <YAxis tick={{ fontSize: 10, fill: "#8b8fa3" }} domain={["auto", "auto"]}
                         width={44} />
                  <Tooltip
                    contentStyle={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 8, fontSize: 12 }}
                    formatter={(v, name) => [v?.toFixed(1), name === "spy" ? benchLabel : "Signal"]} />
                  <Line type="monotone" dataKey="spy" stroke="#7c8494" strokeWidth={1.5}
                        strokeDasharray="5 3" dot={false} />
                  <Line type="monotone" dataKey="nav" stroke={lineColor} strokeWidth={2}
                        dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {bt.universe} · {bt.n_events} events · {bt.method}
            </p>
            {bt.book && (
              <div data-testid={`${slug}-bt-book`}>
                <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                  Current portfolio —{" "}
                  {bt.slots
                    ? `${bt.book.open.length} of ${bt.slots} slots invested · ${Math.round(
                        (1 - bt.book.open.length / bt.slots) * 100)}% in cash`
                    : `${bt.book.open.length} open positions · equal weight`}
                </div>
                {bt.book.open.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2"
                     data-testid={`${slug}-bt-book-empty`}>
                    No open positions — the book is fully in cash, waiting for fresh signals.
                  </p>
                ) : (
                  <div className="overflow-x-auto max-h-80 overflow-y-auto"
                       data-testid={`${slug}-bt-book-open-table`}>
                    <table className="w-full text-xs">
                      <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
                        <tr>
                          <th className="py-1.5 px-2 text-left">Symbol</th>
                          <th className="py-1.5 px-2 text-left">Tier</th>
                          <th className="py-1.5 px-2 text-left">Fund</th>
                          <th className="py-1.5 px-2 text-right">Entry</th>
                          <th className="py-1.5 px-2 text-right">Entry px</th>
                          <th className="py-1.5 px-2 text-right">Held</th>
                          <th className="py-1.5 px-2 text-right">Weight</th>
                          <th className="py-1.5 px-2 text-right">Return</th>
                          <th className="py-1.5 px-2 text-right">vs {benchLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bt.book.open.map((p) => (
                          <tr key={`${p.symbol}-${p.entry_date}-${p.fund}`}
                              className="border-b border-border/40">
                            <td className="py-1.5 px-2 font-semibold">{p.symbol}</td>
                            <td className="py-1.5 px-2">
                              <Badge variant="outline" className="text-[10px]">{p.tier}</Badge>
                            </td>
                            <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[180px]">{p.fund}</td>
                            <td className="py-1.5 px-2 text-right font-mono">{p.entry_date}</td>
                            <td className="py-1.5 px-2 text-right font-mono">{p.entry_px ?? "—"}</td>
                            <td className="py-1.5 px-2 text-right font-mono">{p.days_held}/{p.hold_max}</td>
                            <td className="py-1.5 px-2 text-right font-mono">
                              {bt.slots ? `${Math.round(100 / bt.slots)}%` : "EW"}
                            </td>
                            <td className={`py-1.5 px-2 text-right font-mono ${
                              p.ret >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{pct(p.ret)}</td>
                            <td className={`py-1.5 px-2 text-right font-mono ${
                              p.excess >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{pct(p.excess)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {bt.book?.closed?.length > 0 && (
              <div data-testid={`${slug}-bt-book-closed`}>
                <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                  Closed positions — last {bt.book.closed.length} completed 63-session holds
                </div>
                <div className="overflow-x-auto max-h-80 overflow-y-auto"
                     data-testid={`${slug}-bt-book-closed-table`}>
                  <table className="w-full text-xs">
                    <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
                      <tr>
                        <th className="py-1.5 px-2 text-left">Symbol</th>
                        <th className="py-1.5 px-2 text-left">Tier</th>
                        <th className="py-1.5 px-2 text-left">Fund</th>
                        <th className="py-1.5 px-2 text-right">Entry</th>
                        <th className="py-1.5 px-2 text-right">Exit</th>
                        <th className="py-1.5 px-2 text-right">Realized</th>
                        <th className="py-1.5 px-2 text-right">vs {benchLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bt.book.closed.map((p) => (
                        <tr key={`${p.symbol}-${p.entry_date}-${p.fund}`}
                            className="border-b border-border/40">
                          <td className="py-1.5 px-2 font-semibold">{p.symbol}</td>
                          <td className="py-1.5 px-2">
                            <Badge variant="outline" className="text-[10px]">{p.tier}</Badge>
                          </td>
                          <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[180px]">{p.fund}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{p.entry_date}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{p.exit_date}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${
                            p.ret >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{pct(p.ret)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${
                            p.excess >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{pct(p.excess)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1"
                 data-testid={`${slug}-bt-attribution`}>
              <AttrList
                title={a.by_tier[0]?.tier?.length <= 2 ? "Contribution by tier" : "Contribution by conviction"}
                rows={a.by_tier} keyField="tier" testid={`${slug}-bt-attr-tier`} />
              <AttrList title="Avg sector exposure" rows={a.sector_exposure_avg}
                        keyField="sector" testid={`${slug}-bt-attr-sector`} />
              <AttrList title="Top fund contributors" rows={a.by_fund.slice(0, 8)}
                        keyField="fund" testid={`${slug}-bt-attr-fund`} />
              <div className="space-y-3">
                <AttrList title="Best names" rows={a.top_symbols.slice(0, 4)}
                          keyField="symbol" testid={`${slug}-bt-attr-best`} />
                <AttrList title="Worst names" rows={a.worst_symbols.slice(0, 4)}
                          keyField="symbol" testid={`${slug}-bt-attr-worst`} />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
